import { useCallback, useEffect, useRef, useState } from 'react';
import apiFetch from './api';

const STORAGE_PREFIX = 'awp_cache:';
const DEFAULT_TTL = 5 * 60 * 1000;

const memCache = new Map();
const inflight = new Map();

const now = () => Date.now();

const readPersisted = (key) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry.expires !== 'number') return null;
    if (entry.expires <= now()) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
};

const writePersisted = (key, entry) => {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exceeded — evict oldest then retry once
    try {
      evictOldestPersisted(5);
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch {
      /* give up silently */
    }
  }
};

const evictOldestPersisted = (count = 5) => {
  const entries = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    try {
      const v = JSON.parse(localStorage.getItem(k));
      entries.push({ k, expires: v?.expires ?? 0 });
    } catch { /* ignore */ }
  }
  entries.sort((a, b) => a.expires - b.expires);
  entries.slice(0, count).forEach(({ k }) => localStorage.removeItem(k));
};

const setEntry = (key, data, ttl) => {
  const entry = { data, expires: now() + ttl };
  memCache.set(key, entry);
  writePersisted(key, entry);
  return entry;
};

const getEntry = (key) => {
  const mem = memCache.get(key);
  if (mem && mem.expires > now()) return mem;
  if (mem) memCache.delete(key);
  const persisted = readPersisted(key);
  if (persisted) {
    memCache.set(key, persisted);
    return persisted;
  }
  return null;
};

export const readCached = (url) => {
  const entry = getEntry(url);
  return entry ? entry.data : undefined;
};

export const fetchJSON = async (url, { ttl = DEFAULT_TTL, forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    const entry = getEntry(url);
    if (entry) return entry.data;
  }

  if (!forceRefresh && inflight.has(url)) {
    return inflight.get(url);
  }

  const promise = (async () => {
    const response = await apiFetch(url);
    if (!response.ok) {
      const err = new Error(`Request failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    const data = await response.json();
    setEntry(url, data, ttl);
    return data;
  })()
    .finally(() => {
      inflight.delete(url);
    });

  inflight.set(url, promise);
  return promise;
};

export const invalidate = (matcher) => {
  const matches = (k) =>
    typeof matcher === 'string' ? k.startsWith(matcher) : matcher.test(k);

  for (const key of Array.from(memCache.keys())) {
    if (matches(key)) memCache.delete(key);
  }
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
      const apiKey = k.slice(STORAGE_PREFIX.length);
      if (matches(apiKey)) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
};

export const clearAllCache = () => {
  memCache.clear();
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
};

/**
 * React hook: SWR-style cached fetch.
 * Returns cached data synchronously on mount, then refreshes in background if stale.
 */
export const useCachedFetch = (url, { ttl = DEFAULT_TTL, enabled = true } = {}) => {
  const initial = enabled && url ? readCached(url) : undefined;
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(enabled && !!url && initial === undefined);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled || !url) return;
      const cached = !force ? readCached(url) : undefined;
      if (cached !== undefined) {
        if (mountedRef.current) {
          setData(cached);
          setLoading(false);
          setError(null);
        }
        return cached;
      }
      if (mountedRef.current) setLoading(true);
      try {
        const result = await fetchJSON(url, { ttl, forceRefresh: force });
        if (mountedRef.current) {
          setData(result);
          setError(null);
        }
        return result;
      } catch (err) {
        if (mountedRef.current) setError(err);
        throw err;
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [url, ttl, enabled]
  );

  useEffect(() => {
    if (!enabled || !url) return;
    load().catch(() => { /* error state already set */ });
  }, [url, enabled, load]);

  const refetch = useCallback(() => load({ force: true }), [load]);

  return { data, loading, error, refetch };
};
